import { yupResolver } from '@hookform/resolvers/yup'
import { EnvelopeClosedIcon, LinkedInLogoIcon } from '@radix-ui/react-icons'
import { Phone } from 'phosphor-react'
import { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { Button } from '../../components/Button'
import { Medias } from '../../enumns/medias'
import { useMediaQuery } from '../../hooks/useMediaQuery'
import { useContactUsMutation } from '../../services/hooks/contactUs/mutations/useContactUsMutation'
import { contactUsSchema } from '../../validators/contactUsSchema'
import { Header } from '../landing/components/Header'
import { ErrorModal } from './components/ErrorModal'
import { ContactUsInput } from './components/Input'
import { SuccessfulModal } from './components/SuccessfulModal'

export const ContactUs = () => {
  const [isSuccessfulModalOpen, setIsSuccessfulModalOpen] = useState(false)
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false)
  const isTabletSize = useMediaQuery(`(min-width: ${Medias.LG}px)`)
  const { formatMessage } = useIntl()

  const { mutateAsync, isLoading } = useContactUsMutation()

  const { control, handleSubmit, formState: { errors }, reset } = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    resolver: yupResolver(contactUsSchema),
    defaultValues: {
      name: '',
      company: '',
      email: '',
      phone: '',
      message: '',
    },
  })

  const onSubmit = (data: any) => {
    mutateAsync(data).then(() => {
      setIsSuccessfulModalOpen(true)
    }).catch(() => {
      setIsErrorModalOpen(true)
    }).finally(() => {
      reset()
    })
  }

  return (
    <>
      <Header />
      <div className="flex h-full min-h-screen bg-[#A3CBE1]">
        {isTabletSize && (
          <aside className="flex flex-col px-[88px] min-h-screen py-[88px] mt-[88px] h-full max-w-[596px] w-full justify-center items-start bg-[#A3CBE1]">
            <h1 className="font-openSans mb-8 text-2xl font-semibold text-[#001D2F]">{formatMessage({ id: 'FEEL_FREE_TO_CALL_US_OR_SEND_AN_E-MAIL' })}</h1>
            <div className='flex'>
              <EnvelopeClosedIcon className="w-6 h-6 text-[#001D2F] mr-2" />
              <p className="font-montserrat mb-8 text-base font-semibold text-[#001D2F]">
                contato@trustinsight.com.br
              </p>
            </div>
            <div className='flex mb-[100px]'>
              <Phone className="w-6 h-6 text-[#001D2F] mr-2" />
              <p className="font-montserrat text-base font-semibold text-[#001D2F]">
                +55 11 3539-3292
              </p>
            </div>
            <a href="https://www.linkedin.com/company/trust-insight/" >
              <LinkedInLogoIcon className="w-10 h-10 text-[#001D2F]" />
            </a>
          </aside>
        )}
        <main className="flex flex-col items-center lg:justify-center lg:py-[88px] py-24 mt-[88px] w-full px-8 bg-[#CEE2ED]">
          <h1 className="font-openSans mb-[87px] text-2xl sm:text-5xl font-bold text-[#042f49]">{formatMessage({ id: 'CONTACT_US' })}</h1>
          <form onSubmit={handleSubmit(onSubmit)} className='flex flex-col items-center w-full max-w-[616px]'>
            <div className="mb-4 w-full">
              <Controller
                name="name"
                control={control}
                render={({
                  field: { value = '', onChange },
                }) => (
                  <ContactUsInput
                    placeholder={formatMessage({ id: 'NAME*' })}
                    value={value}
                    onChange={onChange}
                    errorMessage={errors.name?.message}
                  />
                )}
              />
            </div>
            <div className="mb-4 w-full">
              <Controller
                name="company"
                control={control}
                render={({
                  field: { value = '', onChange },
                }) => (
                  <ContactUsInput
                    placeholder={formatMessage({ id: 'COMPANY' })}
                    value={value}
                    onChange={onChange}
                    errorMessage={errors.company?.message}
                  />
                )}
              />
            </div>

            <div className="flex gap-x-6 mb-4 w-full">
              <div className="w-full">
                <Controller
                  name="email"
                  control={control}
                  render={({
                    field: { value = '', onChange },
                  }) => (
                    <ContactUsInput
                      placeholder={formatMessage({ id: 'EMAIL*' })}
                      value={value}
                      onChange={onChange}
                      errorMessage={errors.email?.message}
                    />
                  )}
                />
              </div>
              <div className="w-full">
                <Controller
                  name="phone"
                  control={control}
                  render={({
                    field: { value = '', onChange },
                  }) => (
                    <ContactUsInput
                      placeholder={formatMessage({ id: 'PHONE*' })}
                      value={value}
                      onChange={onChange}
                      errorMessage={errors.phone?.message}
                    />
                  )}
                />
              </div>
            </div>
            <div className="mb-4 w-full">
              <Controller
                name="message"
                control={control}
                render={({
                  field: { value = '', onChange },
                }) => (
                  <ContactUsInput
                    type="textarea"
                    placeholder={formatMessage({ id: 'MESSAGE*' })}
                    value={value}
                    onChange={onChange}
                    errorMessage={errors.message?.message}
                  />
                )}
              />
            </div>
            <Button
              type="submit"
              isLoading={isLoading}
              className="font-openSans font-semibold w-60 h-10 bg-[#042F49]"
            >
              {formatMessage({ id: 'SEND' })}
            </Button>
          </form>
        </main>
      </div>
      {isSuccessfulModalOpen && (
        <SuccessfulModal
          isOpen={isSuccessfulModalOpen}
          onClose={() => setIsSuccessfulModalOpen(false)}
        />
      )}
      {isErrorModalOpen && (
        <ErrorModal
          isOpen={isErrorModalOpen}
          onClose={() => setIsErrorModalOpen(false)}
        />
      )}
    </>
  )
}
